import Dragger from "@/appComponents/Blocks/Utils/Dragger/Dragger";
import FocusTrap from "focus-trap-react";
import { cardFocusTrapOptions } from "@/appComponents/Blocks/Cards/CardWrapper/CardWrapper";
import { Divider, Overlay, Flex, Alert } from "@theorgchartdev/storybook";
import React from "react";
import { useRecordInfoContext } from "@/providers/RecordInfoProvider";
import { RecordDisplayLabel } from "@/appComponents/Blocks/Composites/RecordDisplayLabel/RecordDisplayLabel";
import { getMainDisplayFieldIds } from "@/Utils/getMainDisplayFieldIds";
import { DataSourceRecordDetails } from "@/appComponents/Blocks/Cards/SourceInfoCard/DataSourceDetails";
import { GetDoc } from "@/Utils/StateLib";
import { LayoutRecordDetails } from "@/appComponents/Blocks/Cards/SourceInfoCard/LayoutRecordDetails";
import { ModifiedRecordFields } from "@/appComponents/Blocks/Cards/SourceInfoCard/ModifiedRecordFields";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import useResizable from "@/hooks/useResizable";
import cx from "classnames";
import { appstr } from "@coreapp/utils/appstr";
import { enumObjectTypes } from "@coreapp/constants";
import { getContainerHeight, getContainerWidth } from "@/Utils/getContainerSize";

export const SourceInfoCard = ({
  initialWidth = 400,
  initialHeight = 400,
  minWidth = 400,
  minHeight = 400
}) => {
  const baseClass = "oc-record-info-card";

  const { showSourceInfo, closeSourceInfo, selectedBox } = useRecordInfoContext();

  const { canViewRecordSourceDetails } = usePermissionCheck();

  const { size, handleMouseDown } = useResizable(400, 400);

  const onClose = () => {
    closeSourceInfo();
  };

  const doc = GetDoc();
  const targetBox = selectedBox;

  if (!doc || !showSourceInfo || !targetBox || !canViewRecordSourceDetails) {
    return;
  }

  const className = `${baseClass}-collapsed`;

  const initOffset = {
    x: (getContainerWidth() - initialWidth) / 2,
    y: (getContainerHeight() - initialHeight) / 2
  };

  const recordObject = targetBox.GetDataObj();
  if (!recordObject) return;

  const detailsFD = recordObject.GetFieldDefByID("_objInfo");
  const recordDetails = detailsFD ? recordObject.GetFieldValue(detailsFD) : null;
  const recordData = recordObject?.JSON() ?? {};
  const layout = targetBox.GetLayout();

  const iwiz = doc.GetIWizData();
  const isPositionChart = doc.IsPositionDocument();
  const isPositionRecord = recordObject.IsPositionObj();

  const mapping = iwiz?.fMapping ?? {};
  const mainFieldIds = getMainDisplayFieldIds(
    isPositionChart,
    isPositionRecord ? enumObjectTypes.position : enumObjectTypes.holder
  );

  const getMappedFieldValue = fieldId =>
    fieldId ? (recordData[mapping[fieldId] || fieldId] ?? "") : "";
  const name = getMappedFieldValue(mainFieldIds.name);
  const id = getMappedFieldValue(mainFieldIds.id);
  const title = getMappedFieldValue(mainFieldIds.title);
  const dept = getMappedFieldValue(mainFieldIds.dept);

  const columnGap = 16;
  const columns = 1;

  const sourceGridStyle = {
    display: "grid",
    gridTemplateColumns: `repeat(${String(columns)}, max-content)`,
    columnGap: `${columnGap}px`,
    gridAutoFlow: "row",
    width: "100%"
  };

  return (
    <Dragger fixed initOffset={initOffset}>
      {ref => (
        <FocusTrap active={showSourceInfo} focusTrapOptions={cardFocusTrapOptions}>
          <Overlay
            ref={ref}
            className={className}
            id={"record-info-card"}
            show={showSourceInfo}
            onClose={onClose}
            headerProps={{
              title: appstr("Source info"),
              headerActions: {
                closeAction: onClose
              }
            }}
            customStyle={{
              width: size.width,
              height: size.height,
              minWidth: minWidth,
              minHeight: minHeight,
              maxWidth: size.width,
              maxHeight: size.height
            }}
          >
            <div className={baseClass}>
              <Flex justifyContent={"space-between"} width={"100%"} alignItems={"flex-start"}>
                <RecordDisplayLabel
                  name={name}
                  id={id}
                  title={title}
                  showTitle={!isPositionRecord}
                  dept={dept}
                  photoSrc={null}
                />
              </Flex>
              <Divider />
              {recordDetails != null ? (
                <Flex
                  direction={"column"}
                  gap={"12px"}
                  alignItems={"flex-start"}
                  style={sourceGridStyle}
                >
                  <DataSourceRecordDetails recordDetails={recordDetails} />
                  <ModifiedRecordFields recordDetails={recordDetails} />
                  <LayoutRecordDetails layout={layout} />
                </Flex>
              ) : (
                <Flex width={"100%"}>
                  <Alert
                    severity={"warning"}
                    showIcon
                    className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
                  >
                    {appstr("Source information not available")}
                  </Alert>
                </Flex>
              )}
            </div>
          </Overlay>
        </FocusTrap>
      )}
    </Dragger>
  );
};
